// src/pages/Navbar.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaEnvelope,
  FaPhone,
  FaFacebookF,
  FaInstagram,
  FaSignInAlt,
  FaSignOutAlt,
  FaUserPlus,
  FaBell,
  FaSun,
  FaMoon,
} from "react-icons/fa";
import { MdOutlineMessage } from "react-icons/md";
import { IoMenuOutline, IoCloseOutline } from "react-icons/io5";

import { useUser } from "../../contexts/UserContext";
import { useNotification } from "../../contexts/NotificationContext";
import { useMessage } from "../../contexts/MessageContext";
import { useContactForm } from "../../contexts/ContactFormContext";

import { getSettings } from "../../services/settingsServices";
import LogoSection from "./LogoSection";
import MegaMenu from "./MegaMenu";

// (optionnel) rend /uploads/... absolu vers l’API
const toAbsolute = (u) => {
  if (!u) return "";
  const s = String(u).trim();
  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:"))
    return s;
  if (!s.startsWith("/uploads")) return s;
  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${s}`;
};

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();

  const { user, updateUser } = useUser();
  const { unreadCount } = useNotification();
  const { unreadMessageCount } = useMessage();
  const { unreadCountContactForm } = useContactForm();

  const [menuOpen, setMenuOpen] = useState(false);
  const [settings, setSettings] = useState(null);

  /* ========= Chargement user depuis localStorage ========= */
  useEffect(() => {
    const storedUserInfo = localStorage.getItem("userInfo");
    if (storedUserInfo && !user) {
      try {
        const userInfo = JSON.parse(storedUserInfo);
        if (userInfo) {
          updateUser(userInfo);
        }
      } catch (error) {
        console.error("Erreur parsing userInfo depuis localStorage", error);
      }
    }
  }, [user, updateUser]);

  /* ========= Chargement settings (contacts, réseaux) ========= */
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        if (data && data.length > 0) {
          setSettings(data[0]);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des settings", error);
      }
    };

    fetchSettings();
  }, []);

  /* ========= Fermer le menu si route change ========= */
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  /* ========= Actions ========= */
  const handleLogout = () => {
    localStorage.clear();
    updateUser(null);
    navigate("/");
  };

  const toggleTheme = () => {
    const theme = document.documentElement.classList.toggle("dark")
      ? "dark"
      : "light";
    localStorage.setItem("theme", theme);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  const avatarUrl = toAbsolute(user?.photoURL) || "/img/user_avatar.jpg";
  const userFullName = user ? `${user.nom || ""} ${user.prenom || ""}`.trim() : "";

  return (
    <>
      {/* BARRE PRINCIPALE */}
      <nav className="fixed z-30 w-full bg-neutral-900/90 backdrop-blur-sm border-b border-white/10 text-white flex items-center justify-between h-[80px] md:h-[96px] shadow-xl">
        {/* Bouton MENU/FERMER à gauche */}
        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          className="flex flex-col items-center justify-center w-[80px] h-[80px] md:w-[96px] md:h-[96px] bg-gradient-to-b from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 cursor-pointer transition-colors"
        >
          <span className="flex items-center justify-center mb-1">
            {menuOpen ? (
              <IoCloseOutline className="w-7 h-7" />
            ) : (
              <IoMenuOutline className="w-7 h-7" />
            )}
          </span>
          <span className="text-[11px] md:text-xs font-semibold tracking-[0.18em] uppercase">
            {menuOpen ? "Fermer" : "Menu"}
          </span>
        </button>

        {/* Logo + nom du comité (cliquable vers /) */}
        <div
          className="flex items-center gap-3 md:gap-5 cursor-pointer px-3 md:px-6"
          onClick={() => handleNavigation("/")}
        >
          <LogoSection />
        </div>

        {/* Icônes de droite */}
        <div className="flex items-center gap-3 md:gap-4 pr-3 md:pr-6">
          {/* Icônes contact (desktop) */}
          {settings && (
            <div className="hidden lg:flex items-center gap-3 text-sm text-slate-200/90">
              <button
                onClick={() => (window.location = `mailto:${settings.mail_comite}`)}
                className="flex items-center gap-2 hover:text-primary transition-colors"
                title="E-mail du comité"
              >
                <FaEnvelope />
                <span>{settings.mail_comite}</span>
              </button>
              <span className="w-px h-4 bg-slate-500/60" />
              <button
                onClick={() => (window.location = `tel:${settings.tel_comite}`)}
                className="flex items-center gap-2 hover:text-primary transition-colors"
                title="Téléphone du comité"
              >
                <FaPhone />
                <span>{settings.tel_comite}</span>
              </button>
              <span className="w-px h-4 bg-slate-500/60" />
              <div className="flex items-center gap-3">
                {settings.facebook_comite && (
                  <FaFacebookF
                    className="cursor-pointer hover:text-primary transition-colors"
                    onClick={() =>
                      window.open(settings.facebook_comite, "_blank", "noreferrer")
                    }
                    title="Facebook"
                  />
                )}
                {settings.instagram_comite && (
                  <FaInstagram
                    className="cursor-pointer hover:text-primary transition-colors"
                    onClick={() =>
                      window.open(settings.instagram_comite, "_blank", "noreferrer")
                    }
                    title="Instagram"
                  />
                )}
              </div>
            </div>
          )}

          {/* Si connecté : notifications, messages, avatar, logout */}
          {user && (
            <>
              {/* Notifications simples (tableau de bord) */}
              <div
                className="relative cursor-pointer"
                onClick={() => handleNavigation("/notifications")}
              >
                <FaBell className="text-lg" title="Notifications" />
                {unreadCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] rounded-full px-1.5 py-0.5">
                    {unreadCount}
                  </span>
                )}
              </div>

              {/* Messages ContactForm (pour rôle admin comité) */}
              {user?.role_id === 1 && (
                <div
                  className="relative cursor-pointer"
                  onClick={() => handleNavigation("/messagerie")}
                >
                  <MdOutlineMessage
                    className="text-lg"
                    title="Messages du formulaire de contact"
                  />
                  {unreadCountContactForm > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] rounded-full px-1.5 py-0.5">
                      {unreadCountContactForm}
                    </span>
                  )}
                </div>
              )}

              {/* Avatar utilisateur */}
              {user?.userId && (
                <div
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={() => handleNavigation("/profile")}
                >
                  <img
                    src={avatarUrl}
                    alt="Avatar utilisateur"
                    className="w-9 h-9 md:w-10 md:h-10 rounded-full object-cover border border-white/20"
                  />
                  <span className="hidden md:inline text-sm max-w-[160px] truncate">
                    {userFullName}
                  </span>
                </div>
              )}

              {/* Logout */}
              <FaSignOutAlt
                className="text-lg cursor-pointer hover:text-primary transition-colors"
                onClick={handleLogout}
                title="Déconnexion"
              />
            </>
          )}

          {/* Si NON connecté : boutons créer compte + login */}
          {!user && (
            <>
              <div
                className="flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full bg-primary text-white hover:bg-primary/90 cursor-pointer transition-all"
                onClick={() => navigate("/register")}
                title="Créer un compte"
              >
                <FaUserPlus className="text-sm" />
              </div>
              <div
                className="flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full bg-primary text-white hover:bg-primary/90 cursor-pointer transition-all"
                onClick={() => navigate("/login")}
                title="Connexion"
              >
                <FaSignInAlt className="text-sm" />
              </div>
            </>
          )}

          {/* Dark / Light mode */}
          <label className="swap swap-rotate cursor-pointer pl-1">
            <input type="checkbox" onChange={toggleTheme} />
            <FaSun className="swap-off w-5 h-5" />
            <FaMoon className="swap-on w-5 h-5" />
          </label>
        </div>
      </nav>

      {/* OVERLAY MENU : uniquement le MegaMenu (plus de "Site Public") */}
      {menuOpen && (
        <div className="fixed top-[80px] md:top-[96px] left-0 right-0 bottom-0 bg-slate-950/80 backdrop-blur-xl z-40 overflow-y-auto">
          <div className="max-w-6xl mx-auto py-6 md:py-8 px-4 md:px-6">
            <MegaMenu
              role_id={user ? user.role_id : null}
              onClose={() => setMenuOpen(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;
